import { z } from 'zod';
import { apenasDigitos } from './documento.ts';

/**
 * Contato e inscrição municipal da pessoa (`.spec/015`, decisão 88).
 *
 * Entraram com a nota fiscal de serviço: a NFS-e leva o e-mail e o telefone do
 * prestador e do tomador, e a inscrição municipal do prestador. Ficam em
 * arquivo próprio pelo mesmo motivo de `endereco.ts` — o conjunto é o mesmo em
 * empresa e em cliente, e a segunda cópia divergiria no primeiro ajuste.
 *
 * **Nenhum é obrigatório**, e é decisão do Adriano (06/10/2026): o cadastro se
 * faz com o que se tem à mão, e quem cobra o que falta é o ato que precisa do
 * dado — a emissão da nota, pelo motor que a emite. Exigir e-mail no cadastro
 * de cliente faria o usuário inventar um para conseguir salvar, e o e-mail
 * inventado é o que a prefeitura usa para mandar a nota.
 *
 * **O fornecedor tem o próprio recorte** (`contatoDoFornecedorSchema`, decisão
 * 115): e-mail e telefone fixo, que são as mesmas colunas do cliente, mais
 * celular e nome do contato — sem a inscrição municipal, que só a NFS-e lê. A
 * regra antiga era "o `PUT` do fornecedor não escreve contato"; ela caiu quando
 * o pedido passou a sair por e-mail e o comprador precisou de onde tirar o
 * endereço. O que continua valendo é o motivo dela: **cada cadastro só escreve
 * o que mostra**. O fornecedor não apaga a inscrição municipal do cliente, e o
 * cliente não apaga o celular e o nome do contato do fornecedor.
 */

/** Teto do e-mail. O RFC admite 254; 200 cobre o real e cabe na tela. */
export const TAMANHO_MAXIMO_EMAIL = 200;

/**
 * E-mail opcional: vazio vira `null`, preenchido é conferido.
 *
 * Minúsculas na gravação: o domínio não diferencia caixa, a caixa da parte
 * local é ignorada por todo provedor em uso, e dois cadastros com
 * `Fiscal@X.com` e `fiscal@x.com` são o mesmo destino escrito duas vezes.
 *
 * A conferência é a do `z.string().email()`, e não uma expressão própria: é a
 * mesma do login, e duas definições de "e-mail válido" no mesmo sistema
 * discordam no primeiro endereço com `+`.
 */
export const emailOpcionalSchema = z
  .string()
  .trim()
  .max(TAMANHO_MAXIMO_EMAIL, `O e-mail tem no máximo ${TAMANHO_MAXIMO_EMAIL} caracteres.`)
  .transform((valor) => (valor.length === 0 ? null : valor.toLowerCase()))
  .nullable()
  .prefault(null)
  .refine(
    (valor) => valor === null || z.string().email().safeParse(valor).success,
    'E-mail inválido.',
  );

/**
 * Telefone opcional: só dígitos, com DDD — dez (fixo) ou onze (celular).
 *
 * A máscara é da tela, como a do CEP e a do documento: gravar `(48) 3433-0000`
 * faria a busca e a integração terem de limpar o que o cadastro sujou. Sem
 * código do país: o sistema é nacional, e o `55` na frente só existiria para
 * ser retirado por quem monta a nota.
 */
export const telefoneOpcionalSchema = z
  .string()
  .transform(apenasDigitos)
  .refine(
    (digitos) => digitos.length === 0 || digitos.length === 10 || digitos.length === 11,
    'Informe o telefone com DDD: 10 ou 11 dígitos.',
  )
  .transform((digitos) => (digitos.length === 0 ? null : digitos))
  .nullable()
  .prefault(null);

/** Teto da inscrição municipal. Cada prefeitura tem o seu formato; 20 cobre todos. */
export const TAMANHO_MAXIMO_INSCRICAO_MUNICIPAL = 20;

/**
 * Inscrição municipal — texto, e não número.
 *
 * Há município que usa dígito verificador com traço, e há o que usa letra. Sem
 * conferência de formato por isso: não existe um algoritmo nacional, e recusar
 * o que a prefeitura emitiu é pior que aceitar um erro de digitação que a
 * própria emissão acusa.
 */
export const inscricaoMunicipalSchema = z
  .string()
  .trim()
  .max(
    TAMANHO_MAXIMO_INSCRICAO_MUNICIPAL,
    `A inscrição municipal tem no máximo ${TAMANHO_MAXIMO_INSCRICAO_MUNICIPAL} caracteres.`,
  )
  .transform((valor) => (valor.length === 0 ? null : valor))
  .nullable()
  .prefault(null);

export const contatoSchema = z.object({
  email: emailOpcionalSchema,
  telefone: telefoneOpcionalSchema,
  inscricaoMunicipal: inscricaoMunicipalSchema,
});

export type Contato = z.infer<typeof contatoSchema>;

/** O mesmo conjunto, na leitura: o que o banco guarda, sem transformação. */
export const contatoRespostaSchema = z.object({
  email: z.string().nullable(),
  telefone: z.string().nullable(),
  inscricaoMunicipal: z.string().nullable(),
});

/**
 * Celular opcional: só dígitos, DDD e os nove do número — onze, sempre.
 *
 * Mais estreito que o `telefone`, que aceita dez ou onze. Ali a coluna é
 * compartilhada com o cliente, que a usa para qualquer número; aqui o campo
 * existe para ser o móvel, e com dez dígitos ele é um fixo no campo errado.
 */
export const celularOpcionalSchema = z
  .string()
  .transform(apenasDigitos)
  .refine(
    (digitos) => digitos.length === 0 || digitos.length === 11,
    'Informe o celular com DDD: 11 dígitos.',
  )
  .transform((digitos) => (digitos.length === 0 ? null : digitos))
  .nullable()
  .prefault(null);

/** Teto do nome do contato: um nome de pessoa, às vezes com o setor ao lado. */
export const TAMANHO_MAXIMO_NOME_CONTATO = 120;

/** Com quem se fala no fornecedor — o vendedor, o balcão. Texto livre. */
export const nomeContatoSchema = z
  .string()
  .trim()
  .max(
    TAMANHO_MAXIMO_NOME_CONTATO,
    `O nome do contato tem no máximo ${TAMANHO_MAXIMO_NOME_CONTATO} caracteres.`,
  )
  .transform((valor) => (valor.length === 0 ? null : valor))
  .nullable()
  .prefault(null);

/**
 * O contato do fornecedor (`.spec/015`, decisão 115).
 *
 * O **telefone fixo é a coluna `telefone`**, a mesma que o cliente chama só de
 * "Telefone", e por isso aceita dez **ou** onze dígitos: a pessoa que é cliente
 * e fornecedor pode ter um celular gravado ali pelo cadastro de cliente, e
 * exigir dez faria a gravação de rotina do fornecedor recusar um campo que
 * ninguém tocou. Uma coluna `telefone_fixo` à parte daria à mesma pessoa dois
 * telefones que ninguém saberia qual vale.
 *
 * Nada é obrigatório, como no resto do contato: quem precisa do e-mail é o
 * envio do pedido, e ele deixa digitar o endereço na hora.
 */
export const contatoDoFornecedorSchema = z.object({
  email: emailOpcionalSchema,
  telefone: telefoneOpcionalSchema,
  celular: celularOpcionalSchema,
  nomeContato: nomeContatoSchema,
});

export type ContatoDoFornecedor = z.infer<typeof contatoDoFornecedorSchema>;

export const contatoDoFornecedorRespostaSchema = z.object({
  email: z.string().nullable(),
  telefone: z.string().nullable(),
  celular: z.string().nullable(),
  nomeContato: z.string().nullable(),
});

/** `4834330000` → `(48) 3433-0000`. Só exibição; nunca gravado. */
export function formatarTelefone(digitos: string | null | undefined): string {
  if (!digitos) return '';

  const limpo = apenasDigitos(digitos);
  if (limpo.length === 11) return `(${limpo.slice(0, 2)}) ${limpo.slice(2, 7)}-${limpo.slice(7)}`;
  if (limpo.length === 10) return `(${limpo.slice(0, 2)}) ${limpo.slice(2, 6)}-${limpo.slice(6)}`;

  return limpo;
}
